import {
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  Input,
  Output,
  ViewChild,
  AfterViewInit,
  OnDestroy
} from '@angular/core';
import SignaturePad from 'signature_pad';
import { LoggerService } from 'src/app/services/logger.service';
import { compressBase64Image, SIGNATURE_COMPRESSION_CONFIG } from '../../shared/utils/image-compression.util';

@Component({
    selector: 'app-signature-pad-create',
    templateUrl: './signature-pad.component.html',
    styleUrls: ['./signature-pad.component.scss'],
    standalone: false
})
export class SignaturePadComponentCreate implements OnInit, AfterViewInit, OnDestroy {
  @Input() type: string;
  @Input() Border_Color: string;
  @Output() signatureSaved: EventEmitter<string | null> = new EventEmitter<string>();

  @ViewChild('signaturePad') canvasEl!: ElementRef<HTMLCanvasElement>;

  signaturePad!: SignaturePad;
  signatureImageData: string | null = null;
  signatureNeeded = false;

  constructor(private logger: LoggerService) {}

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    if (this.canvasEl?.nativeElement) {
      this.initCanvasAndPad();

      // Save on interaction end
      this.canvasEl.nativeElement.addEventListener('mouseup', () => this.savePad());
      this.canvasEl.nativeElement.addEventListener('touchend', () => this.savePad());

      // Orientation + resize listeners
      window.addEventListener('resize', this.handleResize);
      window.addEventListener('orientationchange', this.handleOrientationChange);
    }
  }

  initCanvasAndPad(): void {
    this.resizeCanvas(); // ✅ Resize canvas first
  
    const ratio = window.devicePixelRatio || 1;
    const penScaleFactor = 1 / ratio;
  
    this.signaturePad = new SignaturePad(this.canvasEl.nativeElement, {
      minWidth: 1.5 * penScaleFactor, 
      maxWidth: 3 * penScaleFactor, 
      penColor: 'black'
    });
  
    // Redraw previous image if any
    if (this.signatureImageData) {
      this.drawSavedImage(this.signatureImageData);
    }
  }
  

  handleResize = (): void => {
    this.resizeCanvas(() => {
      if (this.signatureImageData) {
        this.drawSavedImage(this.signatureImageData);
      }
    });
  };

  handleOrientationChange = (): void => {
    setTimeout(() => this.handleResize(), 300);
  };

  getBorderStyle(): string {
    return `1px solid ${this.Border_Color || '#CBDBE4'}`;
  }

  clearSignature(): void {
    this.signaturePad?.clear();
    this.signatureImageData = null;
    this.signatureSaved.emit(null);
  }

  async savePad(): Promise<void> {
    if (!this.signaturePad) return;
    if (!this.signaturePad.isEmpty()) {
      const rawData = this.signaturePad.toDataURL();
      try {
        // Compress to reduce base64 size before emitting
        this.signatureImageData = await compressBase64Image(
          rawData, 
          SIGNATURE_COMPRESSION_CONFIG.MAX_WIDTH, 
          SIGNATURE_COMPRESSION_CONFIG.MAX_HEIGHT
        );
        this.signatureSaved.emit(this.signatureImageData);
      } catch (error) {
        this.logger.error('Error compressing signature:', error);
        // Fallback to raw data if compression fails
        this.signatureImageData = rawData;
        this.signatureSaved.emit(this.signatureImageData);
      }
    }
  }

  drawSavedImage(imageData: string): void {
    const canvas = this.canvasEl.nativeElement;
    const context = canvas.getContext('2d');
    if (!context) return;

    const ratio = window.devicePixelRatio || 1;
    const img = new Image();

    img.onload = () => {
      // 🖼 Draw image scaled to canvas size
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(img, 0, 0, canvas.width / ratio, canvas.height / ratio);
    };
    img.src = imageData;
  }

  resizeCanvas(callback?: () => void): void {
    const canvas = this.canvasEl.nativeElement;
    const context = canvas.getContext('2d');
    if (!context) return;

    const ratio = window.devicePixelRatio || 1;

    const displayWidth = canvas.clientWidth;
    const displayHeight = canvas.clientHeight;

    canvas.width = displayWidth * ratio;
    canvas.height = displayHeight * ratio;
    context.scale(ratio, ratio);

    callback?.();
  }

  ngOnDestroy(): void {
    if (this.canvasEl?.nativeElement) {
      this.canvasEl.nativeElement.removeEventListener('mouseup', this.savePad);
      this.canvasEl.nativeElement.removeEventListener('touchend', this.savePad);
    }
  
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('orientationchange', this.handleOrientationChange);
  }
  
}
